import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { ethers } from 'ethers';
import { Play, Square, RefreshCw, ChevronDown, ChevronUp, Plus, X, Copy } from 'lucide-react';
import { Config, GenesisActivePipeline, GenesisPipelineListItem, GenesisPipelineStatus, GenesisPipelineStatistics } from '../types';
import { apiService } from '../services/api';
import { isValidPrivateKey, formatAddress, formatBalance, getTokenSymbol, isNativeETH } from '../utils/helpers';
import BalanceUSD from './BalanceUSD';
import { useTokenPrice } from '../hooks/useTokenPrice';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const FormCard = styled.div`
  background: var(--bg-primary);
  border-radius: 10px;
  padding: 16px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
`;

const FormCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const FormCardTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.3s ease;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const RemoveButton = styled.button`
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-muted);
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    color: #ef4444;
    border-color: #ef4444;
    background: rgba(239, 68, 68, 0.05);
  }
`;

const CopyFromButton = styled.button`
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-muted);
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    color: var(--accent-primary);
    border-color: var(--accent-primary);
    background: rgba(59, 130, 246, 0.05);
  }
`;

const CopyDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: var(--shadow-md);
  min-width: 200px;
  z-index: 100;
  max-height: 200px;
  overflow-y: auto;
`;

const CopyDropdownItem = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  color: var(--text-primary);
  font-size: 0.8rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: var(--bg-secondary);
  }
`;

const Container = styled.div`
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--shadow-md);
  margin-top: 20px;
  margin-bottom: 24px;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
`;

const Label = styled.label`
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.3s ease;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.8rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: var(--bg-tertiary);
    color: var(--text-muted);
  }
`;

const TextArea = styled.textarea`
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.75rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s ease;
  min-height: 60px;
  resize: vertical;
  font-family: 'Courier New', monospace;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const HelperText = styled.div`
  font-size: 0.7rem;
  color: var(--text-muted);
  margin-top: 2px;
`;

const OperationButtons = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
`;

const OperationButton = styled.button<{ active: boolean; variant: 'buy' | 'sell' }>`
  flex: 1;
  padding: 8px;
  border: 2px solid ${props => props.active
    ? (props.variant === 'buy' ? '#22c55e' : '#ef4444')
    : 'var(--border-color)'
  };
  background: ${props => props.active
    ? (props.variant === 'buy' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)')
    : 'transparent'
  };
  color: ${props => props.active
    ? (props.variant === 'buy' ? '#22c55e' : '#ef4444')
    : 'var(--text-secondary)'
  };
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.variant === 'buy' ? '#22c55e' : '#ef4444'};
  }
`;

const AdvancedToggle = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 0;
  margin-bottom: 6px;
  transition: color 0.2s ease;

  &:hover {
    color: var(--text-primary);
  }
`;

const AdvancedSettings = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 10px;
`;

const ActionButton = styled.button<{ variant: 'start' | 'stop' }>`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;

  background: ${props => props.variant === 'start'
    ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
  };
  color: white;
  box-shadow: ${props => props.variant === 'start'
    ? '0 2px 8px rgba(34, 197, 94, 0.25)'
    : '0 2px 8px rgba(239, 68, 68, 0.25)'
  };

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const BalanceDisplay = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 10px;
  font-size: 0.75rem;
  color: var(--text-secondary);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BalanceLabel = styled.span`
  color: var(--text-muted);
`;

const BalanceValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Courier New', monospace;
`;

const AddressText = styled.div`
  font-size: 0.7rem;
  color: var(--text-muted);
  font-family: 'Courier New', monospace;
  word-break: break-all;
`;

const PipelineStatusBar = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
`;

const PipelineStatusInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 0.8rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  transition: color 0.3s ease;
`;

const RefreshButton = styled.button`
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;

const PipelineCard = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 10px;
  transition: all 0.2s ease;
`;

const PipelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const PipelineInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const PipelineId = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  background: ${props => {
    switch (props.status) {
      case 'running': return '#22c55e';
      case 'starting': return '#f59e0b';
      case 'completed': return '#3b82f6';
      case 'stopped': return '#6b7280';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  }};
`;

const PipelineMeta = styled.span`
  font-size: 0.8rem;
  color: var(--text-muted);
`;

const PipelineActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SmallButton = styled.button<{ variant?: 'stop' | 'default' }>`
  padding: 5px 12px;
  border: 1px solid ${props => props.variant === 'stop' ? '#ef4444' : 'var(--border-color)'};
  background: ${props => props.variant === 'stop' ? 'rgba(239, 68, 68, 0.1)' : 'transparent'};
  color: ${props => props.variant === 'stop' ? '#ef4444' : 'var(--text-secondary)'};
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'stop' ? 'rgba(239, 68, 68, 0.2)' : 'var(--bg-tertiary)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;

  &:hover {
    color: var(--text-primary);
  }
`;

const PipelineDetails = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--border-color);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
`;

const StatCard = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 10px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const StatLabel = styled.div`
  font-size: 0.7rem;
  color: var(--text-muted);
  margin-top: 2px;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 0.8rem;
  margin-bottom: 6px;
  color: var(--text-secondary);
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  min-width: 120px;
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 0.8rem;
  margin-top: 6px;
  padding: 8px;
  background: rgba(239, 68, 68, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(239, 68, 68, 0.15);
`;

const AddWorkersSection = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--border-color);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px;
  color: var(--text-muted);
  font-size: 0.9rem;
`;

// ============================================================================
// FORM CARD COMPONENT — one per pipeline form instance
// ============================================================================

interface GenesisDCAFormCardProps {
  config: Config | null;
  formNumber: number;
  onRemove: () => void;
  onPipelineStarted: (pipelineId: string) => void;
  activePipelineId?: string;
  activePipelineStatus?: string;
  initialSettings?: GenesisActivePipeline;
  allPipelines?: GenesisPipelineListItem[];
  onStop?: (pipelineId: string) => void;
}

export const GenesisDCAFormCard: React.FC<GenesisDCAFormCardProps> = ({
  config, formNumber, onRemove, onPipelineStarted,
  activePipelineId, activePipelineStatus: initialStatus,
  initialSettings, allPipelines, onStop,
}) => {
  const [genesisPrivateKey, setGenesisPrivateKey] = useState('');
  const [workerPrivateKeys, setWorkerPrivateKeys] = useState('');
  const [priceRangeMin, setPriceRangeMin] = useState(1.0);
  const [priceRangeMax, setPriceRangeMax] = useState(5.0);
  const [intervalSeconds, setIntervalSeconds] = useState(300);
  const [maxOperations, setMaxOperations] = useState(100);
  const [firstOperation, setFirstOperation] = useState<'buy' | 'sell'>('buy');
  const [reservePercent, setReservePercent] = useState(20.0);
  const [slippage, setSlippage] = useState(0.5);
  const [maxGasUsd, setMaxGasUsd] = useState(2.0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  // Pipeline state
  const [linkedPipelineId, setLinkedPipelineId] = useState<string | undefined>(activePipelineId);
  const [pipelineStatus, setPipelineStatus] = useState<string | undefined>(initialStatus);
  const [isStopping, setIsStopping] = useState(false);

  // Copy From state
  const [showCopyDropdown, setShowCopyDropdown] = useState(false);
  const copyDropdownRef = useRef<HTMLDivElement>(null);

  // Balance state
  const [genesisAddress, setGenesisAddress] = useState('');
  const [genesisEthBalance, setGenesisEthBalance] = useState<number | null>(null);
  const [genesisTokenBalance, setGenesisTokenBalance] = useState<number | null>(null);
  const isLoadingBalanceRef = useRef(false);

  // USD price data
  const { ethPrice, tokenPrice, isLoading: isPriceLoading } = useTokenPrice(config?.tokenAddress);

  // Active pipeline public info
  const [activeGenesisAddress, setActiveGenesisAddress] = useState('');
  const [activeWorkerAddresses, setActiveWorkerAddresses] = useState<string[]>([]);
  const [activeCurrentWorker, setActiveCurrentWorker] = useState('');
  const [lastWorkerAddress, setLastWorkerAddress] = useState('');

  // Fill from initial settings
  useEffect(() => {
    if (initialSettings) {
      setPriceRangeMin(initialSettings.price_range_usd_min);
      setPriceRangeMax(initialSettings.price_range_usd_max);
      setIntervalSeconds(initialSettings.interval_seconds);
      setMaxOperations(initialSettings.max_operations);
      setFirstOperation(initialSettings.first_operation);
      setReservePercent(initialSettings.reserve_percent);
      setSlippage(initialSettings.slippage);
      setMaxGasUsd(initialSettings.max_gas_usd);
      if (initialSettings.genesis_address) setActiveGenesisAddress(initialSettings.genesis_address);
      if (initialSettings.worker_addresses) setActiveWorkerAddresses(initialSettings.worker_addresses);
      if (initialSettings.current_worker_address) setActiveCurrentWorker(initialSettings.current_worker_address);
    }
  }, [initialSettings]);

  // Sync activePipelineId prop
  useEffect(() => {
    if (activePipelineId) {
      setLinkedPipelineId(activePipelineId);
      setPipelineStatus(initialStatus);
    }
  }, [activePipelineId, initialStatus]);

  // Poll pipeline status when linked
  useEffect(() => {
    if (!linkedPipelineId) return;
    const poll = async () => {
      try {
        const status = await apiService.getGenesisPipelineStatus(linkedPipelineId);
        setPipelineStatus(status.status);
        if (status.genesis_address) setActiveGenesisAddress(status.genesis_address);
        if (status.current_worker_address) setActiveCurrentWorker(status.current_worker_address);
        if (status.status === 'completed' || status.status === 'stopped' || status.status === 'error') {
          // Pipeline finished — сохраняем адреса, очищаем ключи
          setLastWorkerAddress(status.current_worker_address || activeCurrentWorker);
          setLinkedPipelineId(undefined);
          setGenesisPrivateKey('');
          setWorkerPrivateKeys('');
          // НЕ сбрасываем pipelineStatus — оставляем "stopped"/"error" для отображения
        }
      } catch {
        // ignore polling errors
      }
    };
    poll();
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
  }, [linkedPipelineId, activeCurrentWorker]);

  // Close copy dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (copyDropdownRef.current && !copyDropdownRef.current.contains(e.target as Node)) {
        setShowCopyDropdown(false);
      }
    };
    if (showCopyDropdown) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [showCopyDropdown]);

  // Derive genesis address from private key
  useEffect(() => {
    try {
      if (genesisPrivateKey && isValidPrivateKey(genesisPrivateKey)) {
        const w = new ethers.Wallet(genesisPrivateKey);
        setGenesisAddress(w.address);
      } else {
        setGenesisAddress('');
        setGenesisEthBalance(null);
        setGenesisTokenBalance(null);
      }
    } catch {
      setGenesisAddress('');
    }
  }, [genesisPrivateKey]);

  // Fetch genesis balance
  const loadGenesisBalance = useCallback(async () => {
    if (!genesisPrivateKey || !isValidPrivateKey(genesisPrivateKey) || !config?.rpcUrl || !config?.tokenAddress) return;
    if (isLoadingBalanceRef.current) return;
    isLoadingBalanceRef.current = true;
    try {
      const result = await apiService.getBalance(genesisPrivateKey, config.rpcUrl, config.tokenAddress);
      setGenesisEthBalance(result.eth_balance);
      setGenesisTokenBalance(result.token?.balance ?? null);
    } catch (err) {
      console.error('Failed to load genesis balance:', err);
    } finally {
      isLoadingBalanceRef.current = false;
    }
  }, [genesisPrivateKey, config?.rpcUrl, config?.tokenAddress]);

  // Auto-load balance when address appears (from private key), refresh every 30s
  useEffect(() => {
    if (!genesisAddress || !config?.rpcUrl || !config?.tokenAddress) return;
    loadGenesisBalance();
    const interval = setInterval(loadGenesisBalance, 30000);
    return () => clearInterval(interval);
  }, [genesisAddress, loadGenesisBalance, config?.rpcUrl, config?.tokenAddress]);

  // Load balance by public address for active pipeline (no private key needed)
  useEffect(() => {
    if (!activeGenesisAddress || !config?.rpcUrl || !config?.tokenAddress) return;
    if (genesisAddress) return; // already loading via private key
    const loadByAddress = async () => {
      try {
        const result = await apiService.getBalanceByAddress(activeGenesisAddress, config.rpcUrl, config.tokenAddress);
        setGenesisEthBalance(result.eth_balance);
        setGenesisTokenBalance(result.token?.balance ?? null);
      } catch { /* ignore */ }
    };
    loadByAddress();
    const interval = setInterval(loadByAddress, 30000);
    return () => clearInterval(interval);
  }, [activeGenesisAddress, genesisAddress, config?.rpcUrl, config?.tokenAddress]);

  const parseWorkerKeys = (text: string): string[] => {
    return text.split('\n').map(k => k.trim()).filter(k => k.length > 0);
  };

  const handleStart = async () => {
    if (!config?.rpcUrl || !config?.tokenAddress || !config?.ethAddress) {
      toast.error('Set Token, Quote Token, and RPC URL in Global Config first');
      return;
    }

    if (!genesisPrivateKey || !isValidPrivateKey(genesisPrivateKey)) {
      toast.error('Enter a valid Genesis private key (64 hex chars)');
      return;
    }

    const workers = parseWorkerKeys(workerPrivateKeys);
    if (workers.length === 0) {
      toast.error('Enter at least one worker private key');
      return;
    }

    const invalidWorkers = workers.filter(k => !isValidPrivateKey(k));
    if (invalidWorkers.length > 0) {
      toast.error(`${invalidWorkers.length} worker key(s) are invalid`);
      return;
    }

    if (priceRangeMin >= priceRangeMax) {
      toast.error('Price Range Min must be less than Max');
      return;
    }

    setIsStarting(true);
    try {
      const result = await apiService.startGenesisDCA({
        genesis_private_key: genesisPrivateKey,
        worker_private_keys: workers,
        token_address: config.tokenAddress,
        quote_token_address: config.ethAddress,
        network: 'base',
        rpc_url: config.rpcUrl,
        price_range_usd_min: priceRangeMin,
        price_range_usd_max: priceRangeMax,
        interval_seconds: intervalSeconds,
        max_operations: maxOperations,
        first_operation: firstOperation,
        reserve_percent: reservePercent,
        slippage: slippage,
        max_gas_usd: maxGasUsd,
        dex_type: config.dexType ?? 'kyberswap',
        pool_id: config.dexType && config.dexType !== 'kyberswap' ? config.selectedPoolId : undefined,
        hook_address: config.selectedPoolInfo?.hooks ?? undefined,
      });
      toast.success(`Pipeline #${result.pipeline_id} started with ${result.worker_count} workers`);
      setLinkedPipelineId(result.pipeline_id);
      setPipelineStatus('starting');
      onPipelineStarted(result.pipeline_id);
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Failed to start pipeline: ${detail}`);
    } finally {
      setIsStarting(false);
    }
  };

  const handleStop = async () => {
    if (!linkedPipelineId) return;
    setIsStopping(true);
    try {
      await apiService.stopGenesisPipeline(linkedPipelineId);
      toast.success(`Pipeline #${linkedPipelineId} stopped`);
      setPipelineStatus('stopped');
      setLastWorkerAddress(activeCurrentWorker);
      setLinkedPipelineId(undefined);
      setGenesisPrivateKey('');
      setWorkerPrivateKeys('');
      if (onStop) onStop(linkedPipelineId);
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Failed to stop: ${detail}`);
    } finally {
      setIsStopping(false);
    }
  };

  const handleCopyFrom = async (pipeline: GenesisPipelineListItem) => {
    setShowCopyDropdown(false);
    try {
      const data = await apiService.getGenesisPipelineFullData(pipeline.pipeline_id);
      // Copy settings (без ключей — бэк их не отдаёт)
      if (data.price_range_usd_min) setPriceRangeMin(parseFloat(data.price_range_usd_min));
      if (data.price_range_usd_max) setPriceRangeMax(parseFloat(data.price_range_usd_max));
      if (data.interval_seconds) setIntervalSeconds(parseInt(data.interval_seconds));
      if (data.max_operations) setMaxOperations(parseInt(data.max_operations));
      if (data.first_operation) setFirstOperation(data.first_operation);
      if (data.reserve_percent) setReservePercent(parseFloat(data.reserve_percent));
      if (data.slippage) setSlippage(parseFloat(data.slippage));
      if (data.max_gas_usd) setMaxGasUsd(parseFloat(data.max_gas_usd));
      toast.success(`Settings copied from Pipeline #${pipeline.pipeline_id}`);
    } catch {
      toast.error('Failed to load pipeline settings');
    }
  };

  const isConfigReady = config?.rpcUrl && config?.tokenAddress && config?.ethAddress;
  const workerCount = parseWorkerKeys(workerPrivateKeys).length;
  const isActive = linkedPipelineId && (pipelineStatus === 'running' || pipelineStatus === 'starting');
  const isStopped = !isActive && (pipelineStatus === 'stopped' || pipelineStatus === 'error' || pipelineStatus === 'completed') && (activeGenesisAddress || lastWorkerAddress);

  return (
    <FormCard>
      <FormCardHeader>
        <FormCardTitle>
          {linkedPipelineId ? (
            <>
              Pipeline #{linkedPipelineId}
              {pipelineStatus && <StatusBadge status={pipelineStatus}>{pipelineStatus}</StatusBadge>}
            </>
          ) : (
            <>Genesis DCA #{formNumber}</>
          )}
        </FormCardTitle>
        <HeaderActions>
          {allPipelines && allPipelines.length > 0 && !isActive && (
            <div ref={copyDropdownRef} style={{ position: 'relative' }}>
              <CopyFromButton onClick={() => setShowCopyDropdown(!showCopyDropdown)}>
                <Copy size={12} /> Copy From
              </CopyFromButton>
              {showCopyDropdown && (
                <CopyDropdown>
                  {allPipelines.map(p => (
                    <CopyDropdownItem key={p.pipeline_id} onClick={() => handleCopyFrom(p)}>
                      Pipeline #{p.pipeline_id} — {p.status} ({p.worker_count} workers)
                    </CopyDropdownItem>
                  ))}
                </CopyDropdown>
              )}
            </div>
          )}
          {!isActive && (
            <RemoveButton onClick={onRemove}>
              <X size={14} /> Remove
            </RemoveButton>
          )}
        </HeaderActions>
      </FormCardHeader>

      {/* Pipeline Status Bar when active */}
      {isActive && (
        <PipelineStatusBar>
          <PipelineStatusInfo>
            Pipeline #{linkedPipelineId} is {pipelineStatus}
          </PipelineStatusInfo>
        </PipelineStatusBar>
      )}

      {/* Genesis & Workers — адреса когда активен, ключи когда нет */}
      {isActive ? (
        <>
          {/* Active: показываем публичные адреса + баланс */}
          <InputGroup>
            <Label>Genesis Address</Label>
            <Input value={activeGenesisAddress} disabled style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
          </InputGroup>
          {genesisEthBalance !== null && (
            <>
              <BalanceDisplay>
                <BalanceRow>
                  <BalanceLabel>ETH</BalanceLabel>
                  <BalanceValue>{formatBalance(genesisEthBalance, 4)}</BalanceValue>
                </BalanceRow>
                {genesisTokenBalance !== null && (
                  <BalanceRow>
                    <BalanceLabel>Token</BalanceLabel>
                    <BalanceValue>{formatBalance(genesisTokenBalance, 2)}</BalanceValue>
                  </BalanceRow>
                )}
              </BalanceDisplay>
              <BalanceUSD
                quoteBalance={genesisEthBalance}
                tokenBalance={genesisTokenBalance ?? 0}
                quoteSymbol={getTokenSymbol(config?.ethAddress)}
                tokenSymbol={getTokenSymbol(config?.tokenAddress)}
                quotePrice={isNativeETH(config?.ethAddress) ? ethPrice : (getTokenSymbol(config?.ethAddress) === 'USDC' || getTokenSymbol(config?.ethAddress) === 'USDbC' ? 1 : ethPrice)}
                tokenPrice={tokenPrice}
                isPriceLoading={isPriceLoading}
              />
            </>
          )}
          <InputGroup>
            <Label>Workers ({activeWorkerAddresses.length}){activeCurrentWorker && ` — current: ${formatAddress(activeCurrentWorker)}`}</Label>
            <TextArea
              value={activeWorkerAddresses.join('\n')}
              disabled
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
          </InputGroup>
        </>
      ) : (
        <>
          {/* Stopped: показываем genesis address и last worker */}
          {isStopped && (
            <>
              {activeGenesisAddress && (
                <InputGroup>
                  <Label>Genesis Address</Label>
                  <Input value={activeGenesisAddress} disabled style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
                </InputGroup>
              )}
              {lastWorkerAddress && (
                <InputGroup>
                  <Label>Last Worker</Label>
                  <Input value={lastWorkerAddress} disabled style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
                </InputGroup>
              )}
            </>
          )}

          {/* Genesis Private Key */}
          <InputGroup>
            <Label>Genesis Private Key</Label>
            <Input
              type="password"
              placeholder="64 hex chars"
              value={genesisPrivateKey}
              onChange={e => setGenesisPrivateKey(e.target.value.trim())}
            />
          </InputGroup>

          {/* Genesis Balance */}
          {genesisAddress && (
            <BalanceDisplay>
              <AddressText>{formatAddress(genesisAddress)}</AddressText>
              <BalanceRow>
                <BalanceLabel>ETH</BalanceLabel>
                <BalanceValue>{genesisEthBalance !== null ? formatBalance(genesisEthBalance, 4) : '...'}</BalanceValue>
              </BalanceRow>
              {genesisTokenBalance !== null && (
                <BalanceRow>
                  <BalanceLabel>Token</BalanceLabel>
                  <BalanceValue>{formatBalance(genesisTokenBalance, 2)}</BalanceValue>
                </BalanceRow>
              )}
            </BalanceDisplay>
          )}
          {genesisAddress && genesisEthBalance !== null && (
            <BalanceUSD
              quoteBalance={genesisEthBalance}
              tokenBalance={genesisTokenBalance ?? 0}
              quoteSymbol={getTokenSymbol(config?.ethAddress)}
              tokenSymbol={getTokenSymbol(config?.tokenAddress)}
              quotePrice={isNativeETH(config?.ethAddress) ? ethPrice : (getTokenSymbol(config?.ethAddress) === 'USDC' || getTokenSymbol(config?.ethAddress) === 'USDbC' ? 1 : ethPrice)}
              tokenPrice={tokenPrice}
              isPriceLoading={isPriceLoading}
            />
          )}

          {/* Worker Private Keys */}
          <InputGroup>
            <Label>Worker Private Keys ({workerCount})</Label>
            <TextArea
              placeholder={"One private key per line"}
              value={workerPrivateKeys}
              onChange={e => setWorkerPrivateKeys(e.target.value)}
            />
            <HelperText>Workers rotate BUY+SELL, then funds move to next.</HelperText>
          </InputGroup>
        </>
      )}

      {/* Price Range Min */}
      <InputGroup>
        <Label>Price Range Min (USD)</Label>
        <Input
          type="number"
          step="0.01"
          min="0.01"
          value={priceRangeMin}
          onChange={e => setPriceRangeMin(parseFloat(e.target.value) || 0)}
          disabled={!!isActive}
        />
      </InputGroup>

      {/* Price Range Max */}
      <InputGroup>
        <Label>Price Range Max (USD)</Label>
        <Input
          type="number"
          step="0.01"
          min="0.01"
          value={priceRangeMax}
          onChange={e => setPriceRangeMax(parseFloat(e.target.value) || 0)}
          disabled={!!isActive}
        />
      </InputGroup>

      {/* Interval */}
      <InputGroup>
        <Label>Interval (seconds)</Label>
        <Input
          type="number"
          min="10"
          value={intervalSeconds}
          onChange={e => setIntervalSeconds(parseInt(e.target.value) || 10)}
          disabled={!!isActive}
        />
      </InputGroup>

      {/* Max Operations */}
      <InputGroup>
        <Label>Max Operations (per worker)</Label>
        <Input
          type="number"
          min="1"
          value={maxOperations}
          onChange={e => setMaxOperations(parseInt(e.target.value) || 1)}
          disabled={!!isActive}
        />
      </InputGroup>

      {/* First Operation */}
      <Label>First Operation</Label>
      <OperationButtons>
        <OperationButton
          active={firstOperation === 'buy'}
          variant="buy"
          onClick={() => !isActive && setFirstOperation('buy')}
        >
          BUY
        </OperationButton>
        <OperationButton
          active={firstOperation === 'sell'}
          variant="sell"
          onClick={() => !isActive && setFirstOperation('sell')}
        >
          SELL
        </OperationButton>
      </OperationButtons>

      {/* Advanced Settings */}
      <AdvancedToggle onClick={() => setShowAdvanced(!showAdvanced)}>
        {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        Advanced
      </AdvancedToggle>

      {showAdvanced && (
        <AdvancedSettings>
          <InputGroup>
            <Label>Reserve %</Label>
            <Input
              type="number"
              step="1"
              min="0"
              max="100"
              value={reservePercent}
              onChange={e => setReservePercent(parseFloat(e.target.value) || 0)}
              disabled={!!isActive}
            />
          </InputGroup>
          <InputGroup>
            <Label>Slippage %</Label>
            <Input
              type="number"
              step="0.1"
              min="0.1"
              max="50"
              value={slippage}
              onChange={e => setSlippage(parseFloat(e.target.value) || 0.5)}
              disabled={!!isActive}
            />
          </InputGroup>
          <InputGroup>
            <Label>Max Gas (USD)</Label>
            <Input
              type="number"
              step="0.1"
              min="0.1"
              value={maxGasUsd}
              onChange={e => setMaxGasUsd(parseFloat(e.target.value) || 2.0)}
              disabled={!!isActive}
            />
          </InputGroup>
        </AdvancedSettings>
      )}

      {/* Start / Stop Button */}
      {isActive ? (
        <ActionButton
          variant="stop"
          onClick={handleStop}
          disabled={isStopping}
        >
          <Square size={16} />
          {isStopping ? 'Stopping...' : 'STOP'}
        </ActionButton>
      ) : (
        <ActionButton
          variant="start"
          disabled={!isConfigReady || !genesisPrivateKey || workerCount === 0 || isStarting}
          onClick={handleStart}
        >
          <Play size={16} />
          {isStarting ? 'Starting...' : 'START'}
        </ActionButton>
      )}
    </FormCard>
  );
};

// ============================================================================
// PIPELINE LIST COMPONENT — shared, one instance
// ============================================================================

interface GenesisPipelineListProps {
  refreshTrigger: number;
}

export const GenesisPipelineList: React.FC<GenesisPipelineListProps> = ({ refreshTrigger }) => {
  const [pipelines, setPipelines] = useState<GenesisPipelineListItem[]>([]);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);
  const [expandedPipeline, setExpandedPipeline] = useState<string | null>(null);
  const [pipelineStatus, setPipelineStatus] = useState<Record<string, GenesisPipelineStatus>>({});
  const [pipelineStats, setPipelineStats] = useState<Record<string, GenesisPipelineStatistics>>({});
  const [stoppingPipelineId, setStoppingPipelineId] = useState<string | null>(null);

  // Add workers state
  const [addWorkersForPipeline, setAddWorkersForPipeline] = useState<string | null>(null);
  const [newWorkerKeys, setNewWorkerKeys] = useState('');
  const [isAddingWorkers, setIsAddingWorkers] = useState(false);

  const loadPipelines = useCallback(async () => {
    try {
      setIsLoadingPipelines(true);
      const result = await apiService.listGenesisPipelines();
      setPipelines(result.pipelines);
    } catch (err: any) {
      console.error('Failed to load pipelines:', err);
    } finally {
      setIsLoadingPipelines(false);
    }
  }, []);

  const loadPipelineDetails = useCallback(async (pipelineId: string) => {
    try {
      const [status, stats] = await Promise.all([
        apiService.getGenesisPipelineStatus(pipelineId),
        apiService.getGenesisPipelineStatistics(pipelineId),
      ]);
      setPipelineStatus(prev => ({ ...prev, [pipelineId]: status }));
      setPipelineStats(prev => ({ ...prev, [pipelineId]: stats }));
    } catch (err: any) {
      console.error(`Failed to load pipeline ${pipelineId} details:`, err);
    }
  }, []);

  // Poll pipelines every 15 seconds + on refreshTrigger change
  useEffect(() => {
    loadPipelines();
    const interval = setInterval(loadPipelines, 15000);
    return () => clearInterval(interval);
  }, [loadPipelines, refreshTrigger]);

  // Poll expanded pipeline details
  useEffect(() => {
    if (!expandedPipeline) return;
    loadPipelineDetails(expandedPipeline);
    const interval = setInterval(() => loadPipelineDetails(expandedPipeline), 15000);
    return () => clearInterval(interval);
  }, [expandedPipeline, loadPipelineDetails]);

  const handleStop = async (pipelineId: string) => {
    setStoppingPipelineId(pipelineId);
    try {
      await apiService.stopGenesisPipeline(pipelineId);
      toast.success(`Pipeline #${pipelineId} stopped`);
      await loadPipelines();
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Failed to stop: ${detail}`);
    } finally {
      setStoppingPipelineId(null);
    }
  };

  const parseWorkerKeys = (text: string): string[] => {
    return text.split('\n').map(k => k.trim()).filter(k => k.length > 0);
  };

  const handleAddWorkers = async (pipelineId: string) => {
    const keys = parseWorkerKeys(newWorkerKeys);
    if (keys.length === 0) {
      toast.error('Enter at least one worker key');
      return;
    }

    const invalid = keys.filter(k => !isValidPrivateKey(k));
    if (invalid.length > 0) {
      toast.error(`${invalid.length} key(s) are invalid`);
      return;
    }

    setIsAddingWorkers(true);
    try {
      const result = await apiService.addGenesisWorkers(pipelineId, keys);
      toast.success(`Added ${result.added_workers_count} workers (total: ${result.total_workers_count})`);
      setNewWorkerKeys('');
      setAddWorkersForPipeline(null);
      await loadPipelines();
      if (expandedPipeline === pipelineId) {
        await loadPipelineDetails(pipelineId);
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Failed to add workers: ${detail}`);
    } finally {
      setIsAddingWorkers(false);
    }
  };

  const toggleExpand = (pipelineId: string) => {
    setExpandedPipeline(prev => prev === pipelineId ? null : pipelineId);
  };

  return (
    <Container>
      <SectionHeader>
        <SectionTitle>Pipelines</SectionTitle>
        <RefreshButton onClick={loadPipelines} disabled={isLoadingPipelines}>
          <RefreshCw size={14} />
          Refresh
        </RefreshButton>
      </SectionHeader>

      {pipelines.length === 0 ? (
        <EmptyState>No pipelines yet. Add a form above and start one.</EmptyState>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '4px' }}>
        {pipelines.map(pipeline => {
          const isExpanded = expandedPipeline === pipeline.pipeline_id;
          const status = pipelineStatus[pipeline.pipeline_id];
          const stats = pipelineStats[pipeline.pipeline_id];
          const isRunning = pipeline.status === 'running' || pipeline.status === 'starting';

          return (
            <PipelineCard key={pipeline.pipeline_id}>
              <PipelineHeader>
                <PipelineInfo>
                  <PipelineId>Pipeline #{pipeline.pipeline_id}</PipelineId>
                  <StatusBadge status={pipeline.status}>{pipeline.status}</StatusBadge>
                  <PipelineMeta>{pipeline.worker_count} workers</PipelineMeta>
                  <PipelineMeta>{pipeline.total_operations_count} ops</PipelineMeta>
                  <PipelineMeta>${pipeline.total_volume_usd.toFixed(2)}</PipelineMeta>
                </PipelineInfo>
                <PipelineActions>
                  {isRunning && (
                    <SmallButton
                      variant="stop"
                      onClick={() => handleStop(pipeline.pipeline_id)}
                      disabled={stoppingPipelineId === pipeline.pipeline_id}
                    >
                      <Square size={12} />
                      {stoppingPipelineId === pipeline.pipeline_id ? 'Stopping...' : 'Stop'}
                    </SmallButton>
                  )}
                  <ExpandButton onClick={() => toggleExpand(pipeline.pipeline_id)}>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </ExpandButton>
                </PipelineActions>
              </PipelineHeader>

              {/* Pipeline summary line with genesis address, current worker, started */}
              <div style={{ marginTop: '6px', display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {pipeline.genesis_address && (
                  <span>Genesis: {formatAddress(pipeline.genesis_address)}</span>
                )}
                {pipeline.current_worker_address && (
                  <span>Current Worker: #{pipeline.current_worker_index} — {formatAddress(pipeline.current_worker_address)}</span>
                )}
                {pipeline.started_at && pipeline.started_at !== 'None' && (
                  <span>Started: {new Date(pipeline.started_at).toLocaleString()}</span>
                )}
              </div>

              {isExpanded && (
                <PipelineDetails>
                  {/* Statistics */}
                  {stats && (
                    <StatsGrid>
                      <StatCard>
                        <StatValue>{stats.total_operations}</StatValue>
                        <StatLabel>Total Ops</StatLabel>
                      </StatCard>
                      <StatCard>
                        <StatValue>{stats.buy_operations}</StatValue>
                        <StatLabel>Buy Ops</StatLabel>
                      </StatCard>
                      <StatCard>
                        <StatValue>{stats.sell_operations}</StatValue>
                        <StatLabel>Sell Ops</StatLabel>
                      </StatCard>
                      <StatCard>
                        <StatValue>${stats.total_volume_usd.toFixed(2)}</StatValue>
                        <StatLabel>Total Volume</StatLabel>
                      </StatCard>
                      <StatCard>
                        <StatValue>${stats.volume_24h_usd.toFixed(2)}</StatValue>
                        <StatLabel>Volume 24h</StatLabel>
                      </StatCard>
                      <StatCard>
                        <StatValue>${stats.volume_1h_usd.toFixed(2)}</StatValue>
                        <StatLabel>Volume 1h</StatLabel>
                      </StatCard>
                    </StatsGrid>
                  )}

                  {/* Status Details */}
                  {status && (
                    <>
                      <DetailRow>
                        <DetailLabel>Status:</DetailLabel>
                        <StatusBadge status={status.status}>{status.status}</StatusBadge>
                      </DetailRow>
                      {status.current_worker_address && (
                        <DetailRow>
                          <DetailLabel>Current Worker:</DetailLabel>
                          #{status.current_worker_index} — {formatAddress(status.current_worker_address)}
                        </DetailRow>
                      )}
                      <DetailRow>
                        <DetailLabel>Buy Task:</DetailLabel>
                        {status.buy_task_status || '—'}
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Sell Task:</DetailLabel>
                        {status.sell_task_status || '—'}
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Started:</DetailLabel>
                        {status.started_at ? new Date(status.started_at).toLocaleString() : '—'}
                      </DetailRow>

                      {/* Active Tasks */}
                      {Object.keys(status.active_tasks).length > 0 && (
                        <>
                          {Object.entries(status.active_tasks).map(([taskId, task]) => (
                            <DetailRow key={taskId}>
                              <DetailLabel>{taskId.includes('buy') ? 'BUY' : 'SELL'}:</DetailLabel>
                              {task.status} | {task.operations_count} ops
                              {task.last_operation && ` | ${task.last_operation}`}
                            </DetailRow>
                          ))}
                        </>
                      )}

                      {status.last_error && (
                        <ErrorText>
                          {status.error_code && `[${status.error_code}] `}
                          {status.last_error}
                        </ErrorText>
                      )}
                    </>
                  )}

                  {/* Add Workers */}
                  {isRunning && (
                    <AddWorkersSection>
                      {addWorkersForPipeline === pipeline.pipeline_id ? (
                        <>
                          <Label>New Worker Keys (one per line)</Label>
                          <TextArea
                            style={{ minHeight: '60px' }}
                            placeholder="Private keys..."
                            value={newWorkerKeys}
                            onChange={e => setNewWorkerKeys(e.target.value)}
                          />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            <SmallButton
                              onClick={() => handleAddWorkers(pipeline.pipeline_id)}
                              disabled={isAddingWorkers}
                            >
                              <Plus size={12} />
                              {isAddingWorkers ? 'Adding...' : 'Add Workers'}
                            </SmallButton>
                            <SmallButton onClick={() => { setAddWorkersForPipeline(null); setNewWorkerKeys(''); }}>
                              Cancel
                            </SmallButton>
                          </div>
                        </>
                      ) : (
                        <SmallButton onClick={() => setAddWorkersForPipeline(pipeline.pipeline_id)}>
                          <Plus size={12} /> Add Workers
                        </SmallButton>
                      )}
                    </AddWorkersSection>
                  )}
                </PipelineDetails>
              )}
            </PipelineCard>
          );
        })}
        </div>
      )}
    </Container>
  );
};

export default GenesisDCAFormCard;
